import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

/**
 * 404 page. Context-aware: logged-in users go to their dashboard,
 * logged-out users go home. Previously this always said "Redirecting to
 * dashboard…" and sent everyone to /dashboard, which bounced
 * unauthenticated visitors straight back to /login, worse than useless.
 */
const NotFound = () => {
  const navigate = useNavigate();
  const { session, loading } = useAuth();
  const isAuthenticated = !loading && !!session;
  const target = isAuthenticated ? "/dashboard" : "/";
  const targetLabel = isAuthenticated ? "Dashboard" : "Home";

  useEffect(() => {
    if (loading) return;
    const timer = setTimeout(() => navigate(target, { replace: true }), 3000);
    return () => clearTimeout(timer);
  }, [navigate, target, loading]);

  return (
    <>
      {/* MS185: NotFound is the SPA catch-all — any URL that doesn't
          match a route lands here. Without `noindex` the page can end
          up in Google's index under hundreds of misspelled / probe /
          typo paths, wasting crawl budget and showing up as a "404"
          search result. Title swap stops social shares of the path
          previewing as if it's a real Lembra page. */}
      <Helmet>
        <title>Page not found. Lembra.</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
    <div className="flex min-h-screen items-center justify-center bg-muted">
      <div className="text-center">
        <h1 className="mb-4 text-4xl font-bold text-navy">404</h1>
        <p className="mb-4 text-xl text-muted-foreground">Page not found</p>
        <p className="mb-6 text-sm text-muted-foreground">
          {loading ? "\u00a0" : `Redirecting to ${targetLabel.toLowerCase()}\u2026`}
        </p>
        <Button
          variant="outline"
          onClick={() => navigate(target, { replace: true })}
          disabled={loading}
        >
          Go to {targetLabel}
        </Button>
      </div>
    </div>
    </>
  );
};

export default NotFound;
